import { rem } from "@certn/cascadia";
import {
  lineHeights,
  sizes,
  textStyles,
  weights,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { createCalendar, getLocalTimeZone } from "@internationalized/date";
import { useRef } from "react";
import {
  useDateField,
  useDateFormatter,
  useDateSegment,
  useLocale,
} from "react-aria";
import { useDateFieldState } from "react-stately";

import { DateInputProps, DateInputSegmentProps } from "./types";
import { FormFeedback } from "../../../components/inputs/_assets/input-wrapper/FormFeedback";
import { FormLabel } from "../../../components/inputs/_assets/input-wrapper/FormLabel";
import { Text } from "../../../foundations/typography/Text";
import { useColors } from "../../../hooks/useColors";

/**
 * @deprecated use DateField instead.
 */
export function DateInput(props: DateInputProps) {
  const { testID = "date-input" } = props;
  const { locale } = useLocale();
  const formatter = useDateFormatter({ dateStyle: "long" });

  const state = useDateFieldState({
    ...props,
    locale,
    createCalendar,
  });

  const ref = useRef<HTMLInputElement>(null);
  const { labelProps, fieldProps, errorMessageProps } = useDateField(
    props,
    state,
    ref
  );
  const filteredStateSegments = state.segments.filter(
    (segment) => segment.type !== "literal"
  );

  return (
    <div data-testid={`${testID}-container`}>
      <FormLabel {...labelProps} isRequired={props.isRequired}>
        {props.label}
      </FormLabel>
      <div
        {...fieldProps}
        ref={ref}
        className={css({ display: "flex", gap: "8px" })}
        data-testid={testID}
      >
        {filteredStateSegments.map((segment, i) => (
          <DateInputSegment
            key={i}
            segment={segment}
            state={state}
            errorMessage={props.errorMessage}
          />
        ))}
      </div>

      {/* human-readable date, locale/language specific */}
      {props.value && (
        <div className={css({ marginTop: "5px" })}>
          <Text style="input/inline-error" color="text-weak">
            {formatter.format(props.value?.toDate(getLocalTimeZone()))}
          </Text>
        </div>
      )}
      <FormFeedback {...errorMessageProps}>{props.errorMessage}</FormFeedback>
    </div>
  );
}

const DateInputSegment = ({
  segment,
  state,
  errorMessage,
  testID = "date-input-segment-container",
}: DateInputSegmentProps) => {
  const colors = useColors();
  const ref = useRef<HTMLInputElement>(null);
  const { segmentProps } = useDateSegment(segment, state, ref);
  const ariaValueMax = segmentProps["aria-valuemax"]; // length 2 = dd/mm, length 4 = yyyy, determines width
  const error = !!errorMessage;
  const values = textStyles["input/text-field"];

  const { locale } = useLocale();
  const displayNames = new Intl.DisplayNames(locale, { type: "dateTimeField" });

  return (
    <div data-testid={testID}>
      <Text
        className={css({
          textTransform: "capitalize",
          display: "grid",
          marginBottom: "var(--space-1)",
        })}
        style="input/helper"
      >
        {displayNames.of(segment.type)}
      </Text>
      <div
        {...segmentProps}
        ref={ref}
        className={css(styles.input, {
          width: ariaValueMax && ariaValueMax <= 31 ? "3.25rem" : "4.5rem",
          textAlign: "center",
          fontSize: rem(sizes[values.size]),
          lineHeight: rem(lineHeights[values.lineHeight]),
          fontWeight: weights[values.weight],
          boxShadow: error
            ? `inset 0 0 0 1px ${colors["border-danger-input"]}`
            : `inset 0 0 0 1px ${colors["border-strong"]}`,
          color: "var(--text-base)",
          fontFamily: "var(--font-family-default)",
          backgroundColor: "var(--background-input)",
          "&:invalid": {
            border: `1px solid ${colors["border-danger-input"]}`,
          },
          "::placeholder": {
            color: "var(--text-placeholder)",
          },
          "&:hover:not(:focus)": !error
            ? {
                borderColor: "var(--border-input-active)",
              }
            : null,
          "&:focus": error
            ? css([
                {
                  outline: "0",
                  borderColor: "var(--border-danger-input)",
                  background: "var(--background-base)",
                  boxShadow: "var(--focus-input-error)",
                },
              ])
            : css([
                {
                  outline: "0",
                  borderColor: "var(--border-input-active)",
                  boxShadow: "var(--focus-input)",
                },
              ]),
        })}
        data-testid={`date-of-birth-${segment.type}-input`}
      >
        <Text color="text-placeholder" style="input/text-field">
          {segment.text.toUpperCase()}
        </Text>
      </div>
    </div>
  );
};

const styles = {
  input: css({
    WebkitAppearance: "none",
    width: "100%",
    boxSizing: "border-box",
    paddingTop: "var(--space-2)",
    paddingRight: "var(--space-3)",
    paddingBottom: "var(--space-2)",
    paddingLeft: "var(--space-3)",
    borderRadius: "var(--border-radius-2)",
    border: "none",
    fontFamily: "var(--font-family-default)",
  }),
};
